import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/Icon';
import { usePermissions } from '@/core/hooks/usePermissions';
import { useLogoutConfirm } from '@/shared/hooks/useLogoutConfirm';

const SideMenuItems = () => {
  const { hasPermission, hasOrganizationId, isAdmin, permissionsLoading } = usePermissions();
  const { t } = useTranslation(['sidebar-menu']);
  const handleLogout = useLogoutConfirm();

  const filterMenuItemsByPermission = (items) => {
    return items.filter((item) => {
      if (item.key === 'user-information') {
        return true;
      }
      if (item.key === '/organization') {
        return hasPermission(item.permission) && hasOrganizationId;
      }
      // Facebook menu - only show for admin role
      if (item.key === 'facebook-group') {
        return isAdmin;
      }
      if (hasPermission(item.permission)) {
        if (item.children && item.children.length > 0) {
          item.children = filterMenuItemsByPermission(item.children);
        }
        return true;
      }

      const requiredPermission = item.permission;

      if (requiredPermission) {
        return hasPermission(requiredPermission);
      }

      if (item.children && item.children.length > 0) {
        const filteredChildren = filterMenuItemsByPermission(item.children);

        if (filteredChildren.length === 0) return false;

        item.children = filteredChildren;
        return true;
      }
      return true;
    });
  };

  // Menu öğelerini oluştur

  const menuItems = [
    {
      key: 'user-information',
      label: t('common:common.accountManager'),
      title: t('common:common.accountManager'),
      children: [
        {
          key: '/profile/profile-information',
          icon: <Icon name="user" size={18} />,
          label: t('sidebar-menu:sidebar-menu.profileInformation'),
          path: '/profile/profile-information',
          title: t('sidebar-menu:sidebar-menu.profileInformation'),
        },
        {
          key: '/profile/notification-preferences',
          icon: <Icon name="bellRing" size={18} />,
          label: t('sidebar-menu:sidebar-menu.notificationPreferences'),
          path: '/profile/notification-preferences',
          title: t('sidebar-menu:sidebar-menu.notificationPreferences'),
        },
        {
          key: 'report-issue',
          icon: <Icon name="flag" size={18} />,
          label: t('sidebar-menu.reportIssue'),
        },
        {
          key: 'updates',
          icon: <Icon name="sparkle" size={18} />,
          label: t('sidebar-menu.updates'),
        },
        {
          key: 'log-out',
          icon: <Icon name="logOut" size={18} />,
          onClick: handleLogout,
          label: t('common:common.logout'),
          title: t('common:common.logout'),
        },
      ],
    },
    {
      key: '/dashboard-panel',
      icon: <Icon name="layoutGrid" />,
      label: t('sidebar-menu:sidebar-menu.dashboard'),
      path: '/dashboard-panel',
      permission: [
        'dashboard_channel_revenue_graph',
        'dashboard_channel_revenue_list',
        'dashboard_channel_watch_count',
        'dashboard_completed_translation_requests',
        'dashboard_content_types_and_counts',
        'dashboard_copyright',
        'dashboard_draft_video',
        'dashboard_published_video',
        'dashboard_top_10_most_watched_channels',
        'dashboard_top_10_publish_channels',
        'dashboard_top_10_upload_channels',
        'dashboard_translation_count',
        'dashboard_translation_request_summary',
        'dashboard_upcoming_translation_deadlines',
        'dashboard_uploaded_video',
        'dashboard_yellow_dollar',
        'dashboard_youtube_channel_and_cms_count',
      ],
    },
    {
      key: '/profile/my-works',
      icon: <Icon name="briefcase" />,
      label: t('sidebar-menu:sidebar-menu.myWorks'),
      path: '/profile/my-works',
      title: t('sidebar-menu:sidebar-menu.myWorks'),
    },
    {
      key: '/performance-reporting',
      icon: <Icon name="fileText" />,
      label: t('sidebar-menu:sidebar-menu.performanceReporting'),
      path: '/performance-reporting',
      permission: ['employee_read', 'performance_calculate'],
    },
    {
      key: '/reports',
      icon: <Icon name="chartPie" />,
      label: t('sidebar-menu:sidebar-menu.reports'),
      path: '/reports',
      permission: ['user_report_read', 'portal_report_read', 'company_report_read'],
    },

    {
      key: 'channel-management-group',
      label: t('sidebar-menu:sidebar-menu.channels'),
      permission: ['channel_read', 'cms_read'],
      children: [
        {
          key: '/cms',
          icon: <Icon name="server" />,
          label: t('sidebar-menu:sidebar-menu.cms'),
          path: '/cms',
          permission: ['cms_read'],
        },
        {
          key: '/mychannels',
          icon: <Icon name="monitorPlay" />,
          label: t('sidebar-menu:sidebar-menu.my-channels'),
          path: '/mychannels',
          permission: ['channel_read'],
        },
        // {
        //   key: '/channelgroups',
        //   icon: <ProfileOutlined />,
        //   label: <Link to="/channelgroups">Kanal Grupları</Link>,
        //   permission: ['channel_group_view'],
        // },
      ],
    },

    {
      key: 'facebook-group',
      label: t('sidebar-menu:sidebar-menu.facebook'),
      permission: ['facebook_channel_read'],
      children: [
        {
          key: '/facebook/accounts',
          icon: <Icon name="circleUser" />,
          label: t('sidebar-menu:sidebar-menu.facebook-accounts'),
          path: '/facebook/accounts',
        },
        {
          key: '/facebook/my-pages',
          icon: <Icon name="layoutPanel" />,
          label: t('sidebar-menu:sidebar-menu.facebook-my-pages'),
          path: '/facebook/my-pages',
          permission: ['facebook_channel_read'],
        },
      ],
    },

    {
      key: 'admin-panel-group',
      label: t('sidebar-menu:sidebar-menu.adminPanel'),
      permission: [
        'user_read',
        'role_read',
        'organization_read',
        'producer_read',
        'licensor_read',
        'product_read',
        'director_read',
        'contract_read',
        'company_read',
        'actor_read',
      ],
      children: [
        {
          key: '/users',
          icon: <Icon name="users" />,
          label: t('sidebar-menu:sidebar-menu.user-management'),
          path: '/users',
          permission: ['user_read'],
        },
        {
          key: '/role-management',
          icon: <Icon name="shieldCheck" />,
          label: t('sidebar-menu:sidebar-menu.role-management'),
          path: '/role-management',
          permission: ['role_read'],
        },
        {
          key: '/organization',
          icon: <Icon name="building" />,
          label: t('sidebar-menu:sidebar-menu.organization'),
          path: '/organization',
          permission: ['organization_read'],
        },
        // {
        //   key: '/company',
        //   icon: <BankFilled />,
        //   label: <Link to="/company">Şirket Yönetimi</Link>,
        //   permission: ['company_view'],
        // },
        {
          key: '/dynamicParameter',
          icon: <Icon name="sliders" />,
          label: t('sidebar-menu:sidebar-menu.parameterManagement'),
          path: '/dynamicParameter',
          permission: [
            'producer_read',
            'licensor_read',
            'product_read',
            'director_read',
            'contract_read',
            'company_read',
            'actor_read',
          ],
        },
        // {
        //   key: '/package',
        //   icon: <ShoppingCartOutlined />,
        //   label: <Link to="/package">Paket Yönetimi</Link>,
        //   permission: ['package_view'],
        // },
        // {
        //   key: '/product',
        //   icon: <ShoppingCartOutlined />,
        //   label: <Link to="/product">Ürün Yönetimi</Link>,
        //   permission: ['product_view'],
        // },
      ],
    },

    /*{
      key: '#',
      label: t('sidebar-menu:sidebar-menu.platform-management'),
      icon: <ToolOutlined />,
      permission: ['project_setting_read', 'release_history_read'],
      children: [
        {
          key: '/project-settings',
          icon: <SettingOutlined />,
          label: t('sidebar-menu:sidebar-menu.projectSettings'),
          //onClick: () => handleMenuClick('/project-settings'),
          permission: ['project_setting_read'],
        },
        {
          key: '/release-history',
          icon: <InfoCircleOutlined />,
          label: t('sidebar-menu:sidebar-menu.releaseHistory'),
          //onClick: () => handleMenuClick('/release-history'),
          permission: ['release_history_read'],
        },
      ],
    },*/
  ];

  // İzinlere göre menü öğelerini filtrele
  const filteredMenuItems = useMemo(
    () => (permissionsLoading ? [] : filterMenuItemsByPermission(menuItems)),
    [permissionsLoading, menuItems]
  );

  return filteredMenuItems;
};

export default SideMenuItems;
